// ─────────────────────────────────────────────────────────────────────────────
// 🔑 A PARTNER'S SUBMISSION, BEFORE ITS BORROWER REGISTERS (Jesse, 2026-11-02).
//
// "Open borrower file" on broker submission #11 ("Broker — awaiting borrower") went to /admin/borrowers/11 and showed
// a blank "Borrower not found": a submission id is not a borrower id. Until the borrower registers there is no borrower
// file — there is this: what the partner sent, their documents, who sent it, and whether the borrower was invited.
//
// 🔑 ONE LINK, BEFORE AND AFTER. Every "Open" on a submission comes here; once the borrower has registered the server
// names them (borrowerUserId) and this page forwards to their file, so a link in an old alert or email still lands.
// Data: GET /api/admin/partners/submissions/:id (utils/staffSubmissionView). Staff wording.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const typeLabel = (t) => (t === 'loan_officer' ? 'Loan officer' : t === 'realtor' ? 'Realtor' : t === 'homebuilder' ? 'Builder' : (t || '—'));
const SECTION_LABEL = { borrower: 'Borrower', coborrower: 'Co-borrower', loan: 'Loan', subject_property: 'Property',
  employment: 'Employment', income: 'Income', assets: 'Assets', credit: 'Credit', housing: 'Housing', rental: 'Rental' };

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '18px 20px', marginBottom: 16 };
const h2 = { fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' };
const row = { display: 'flex', justifyContent: 'space-between', gap: 14, borderBottom: '1px solid #f3f4f6', padding: '7px 0', fontSize: 13 };

export default function AdminSubmissionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resend, setResend] = useState({ busy: false, msg: null, ok: null });
  const keepRecord = new URLSearchParams(window.location.search).get('record') === '1';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/admin/partners/submissions/${encodeURIComponent(id)}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(r.status === 404 ? 'not_found' : (d.error || `HTTP ${r.status}`)); setData(null); return; }
      // 🔑 THE BORROWER HAS REGISTERED: their file is the record now — unless the record itself was asked for
      // (?record=1: the Leads panel's "Open submission record →", Leads redesign 2026-11-02).
      if (d.borrowerUserId && !keepRecord) { navigate(`/admin/borrowers/${Number(d.borrowerUserId)}`, { replace: true }); return; }
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [id, token, navigate, keepRecord]);

  useEffect(() => { load(); }, [load]);

  const doResend = async () => {
    setResend({ busy: true, msg: null, ok: null });
    try {
      const r = await fetch(`${API}/api/admin/partners/submissions/${encodeURIComponent(id)}/resend-invite`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      setResend({ busy: false, ok: r.ok, msg: r.ok ? `Invite sent to ${d.sent_to}.` : (d.error || 'The invite could not be sent.') });
      if (r.ok) load();
    } catch (e) { setResend({ busy: false, ok: false, msg: e.message }); }
  };

  const openDoc = async (docId) => {
    const r = await fetch(`${API}/api/admin/partners/submissions/${encodeURIComponent(id)}/documents/${docId}/file`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { const d = await r.json().catch(() => ({})); alert(d.error || 'That file could not be opened.'); return; }
    const url = URL.createObjectURL(await r.blob());
    window.open(url, '_blank', 'noopener');
  };

  if (loading) {
    return <AdminLayout><div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading submission…</div></AdminLayout>;
  }
  if (error || !data) {
    // ⚠️ NEVER A DEAD END: say what it is and where to look.
    return (
      <AdminLayout>
        <div data-testid="submission-missing" style={{ padding: '32px', maxWidth: 640 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
            {error === 'not_found' ? `Submission #${id} isn't available` : 'This submission could not be loaded'}
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
            {error === 'not_found'
              ? 'It does not exist, or it belongs to a partner outside your book.'
              : String(error)}
          </p>
          <button type="button" onClick={() => navigate('/admin/partners/referrals')}
            style={{ padding: '8px 14px', borderRadius: 8, background: '#3a6ad9', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Partner Referrals →
          </button>
        </div>
      </AdminLayout>
    );
  }

  const awaiting = data.state === 'awaiting_registration';
  const bySection = data.fields.reduce((m, f) => { (m[f.section] = m[f.section] || []).push(f); return m; }, {});

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: 980 }}>
        <button type="button" onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 10 }}>
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
          <h1 data-testid="submission-title" style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
            {data.borrower.name || data.borrower.email || 'Unnamed borrower'}
          </h1>
          <span data-testid="submission-state" style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 12,
            background: awaiting ? '#fffbeb' : '#f3f4f6', color: awaiting ? '#7a5200' : '#374151' }}>
            {data.stateLabel}
          </span>
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
          Broker submission #{data.id} · submitted {fmtDate(data.submittedAt)}
          {data.expiresAt ? ` · expires ${fmtDate(data.expiresAt)}` : ''}
        </p>
        {keepRecord && data.borrowerUserId && (
          <div data-testid="submission-registered" style={{ ...card, background: '#f9fafb' }}>
            <div style={{ fontSize: 13, color: '#374151' }}>
              {data.borrower.name || 'The borrower'} has registered, so their borrower file is the record now.{' '}
              <button type="button" onClick={() => navigate(`/admin/borrowers/${Number(data.borrowerUserId)}`)}
                style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                Open borrower file →
              </button>
            </div>
          </div>
        )}
        {awaiting && (
          <div data-testid="submission-awaiting" style={{ ...card, background: '#fffbeb', borderColor: '#fed7aa' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#7a5200', marginBottom: 4 }}>Awaiting borrower registration</div>
            <div style={{ fontSize: 13, color: '#374151' }}>
              There is no borrower file yet. It is created when {data.borrower.name || 'the borrower'} registers from the
              invite and confirms what the partner sent. Until then this is the record.
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <div style={card} data-testid="submission-partner">
            <div style={h2}>Partner</div>
            <div style={row}><span style={{ color: '#6b7280' }}>Name</span><span style={{ fontWeight: 600 }}>{data.partner ? data.partner.name : '—'}</span></div>
            <div style={row}><span style={{ color: '#6b7280' }}>Email</span><span>{data.partner ? data.partner.email : '—'}</span></div>
            <div style={row}><span style={{ color: '#6b7280' }}>Type</span><span>{data.partner ? typeLabel(data.partner.type) : '—'}</span></div>
            <div style={row}><span style={{ color: '#6b7280' }}>Firm</span><span>{(data.partner && data.partner.company) || '—'}</span></div>
            {data.partner && (
              <button type="button" onClick={() => navigate(`/admin/partners/partners?partner=${data.partner.id}`)}
                style={{ marginTop: 10, background: 'none', border: 'none', color: '#3a6ad9', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                Open partner →
              </button>
            )}
          </div>
          <div style={card} data-testid="submission-invite">
            <div style={h2}>Borrower invite</div>
            <div style={row}><span style={{ color: '#6b7280' }}>Borrower email</span><span>{data.borrower.email || '—'}</span></div>
            <div style={row}><span style={{ color: '#6b7280' }}>Invite</span>
              <span>{data.invite.sentAt ? `Sent ${fmtDate(data.invite.sentAt)}` : 'Not sent'}
                {data.invite.timesSent > 0 ? ` · sent ${data.invite.timesSent} ${data.invite.timesSent === 1 ? 'time' : 'times'}` : ''}</span></div>
            {data.invite.canResend && (
              <button type="button" data-testid="submission-resend" onClick={doResend} disabled={resend.busy}
                style={{ marginTop: 12, padding: '8px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
                  background: resend.busy ? '#e5e7eb' : '#3a6ad9', color: resend.busy ? '#9ca3af' : '#fff', cursor: resend.busy ? 'not-allowed' : 'pointer' }}>
                {resend.busy ? 'Sending…' : 'Resend invite'}
              </button>
            )}
            {resend.msg && (
              <div data-testid="submission-resend-msg" style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: resend.ok ? '#2d6a0a' : '#ff082f' }}>{resend.msg}</div>
            )}
          </div>
        </div>

        <div style={card} data-testid="submission-documents">
          <div style={h2}>Documents</div>
          {data.documents.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>No documents were attached.</div>
          ) : data.documents.map((d) => (
            <div key={d.id} style={row}>
              <span>{d.original_name} <span style={{ color: '#9ca3af', fontSize: 12 }}>· {String(d.document_type || '').replace(/_/g, ' ')} · {fmtDate(d.uploaded_at)}</span></span>
              {d.has_file
                ? <button type="button" onClick={() => openDoc(d.id)} style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Open</button>
                : <span style={{ color: '#9ca3af', fontSize: 12 }}>No longer held</span>}
            </div>
          ))}
        </div>

        <div style={card} data-testid="submission-fields">
          <div style={h2}>What the partner submitted</div>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>{data.note}</p>
          {data.fields.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>No fields recorded.</div>
          ) : Object.entries(bySection).map(([sec, list]) => (
            <div key={sec} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '8px 0 4px' }}>{SECTION_LABEL[sec] || sec}</div>
              {list.map((f) => (
                <div key={f.key} style={row}>
                  <span style={{ color: '#6b7280' }}>{f.label}</span>
                  <span style={{ fontWeight: 600, color: '#111827', textAlign: 'right', wordBreak: 'break-word' }}>{f.value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
